import type {ReactElement} from 'react';
import React, {useState} from 'react';
import {removeImageBackground} from '@Utils/image.util';
import {isUserPremium} from '@Libraries/user.library';
import {setIsBackgroundRemoved, setShowLoading} from '@Panels/cropper-panel/cropper-panel-reducer';
import {IconBoxWithTag} from '@Components/icon-box-with-tag';
import {MODAL_IDS, openModal} from '@Components/modal-container-deprecated';
import {updateTeamCredits} from '@Components/team-credits/team-credits-slice';
import {updatePAYGUserImageBGRCredits} from '@Components/payg-user-credits/payg-user-credits-slice';
import styles from './remove-background-icon-box.module.scss';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {
  getCreditsForImageBGR,
  isImageBGRAllowedForPAYGUser
} from '@Libraries/bg-removals-library';
import {noop} from "@Utils/general.util";

/**
 * @author Ahmed Ateeq @ahmedateeq@250mils.com
 */

interface RemoveBackgroundIconBoxProps {
  className?: string;

  imgHashedFilename: string;
}

export function RemoveBackgroundIconBox({className = '', ...props}: RemoveBackgroundIconBoxProps): ReactElement {
  const dispatch = useAppDispatch();
  const isBackgroundRemoved = useAppSelector((state) => {
    return state.cropperPanel.isBackgroundRemoved;
  });
  const [isBackgroundRemovalInProgress, setIsBackgroundRemovalInProgress] = useState(false);
  const isPremiumUser = isUserPremium();
  const backgroundRemovalCredits = getCreditsForImageBGR();
  const isBGRAllowedForPAYG = isImageBGRAllowedForPAYGUser();

  const showPremiumOnlyFeatureDialog = (): void => {
    void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_BG_REMOVALS, noop, true);
  };

  const showErrorModal = (message: string): void => {
    openModal(MODAL_IDS.ERROR_MODAL_ID, {
      message,
    });
  };

  const insufficientCredits = (): boolean => {
    return backgroundRemovalCredits === 0;
  };

  const onBackgroundRemoveClicked = async (): Promise<void> => {
    if (!isBackgroundRemoved) {
      if (!isUserPremium() && !isBGRAllowedForPAYG) {
        showPremiumOnlyFeatureDialog();
        return;
      }

      if (insufficientCredits()) {
        if (isPremiumUser) {
          showErrorModal('pmwjs_premium_not_enough_credits');
        } else {
          showPremiumOnlyFeatureDialog();
        }
        return;
      }

      setIsBackgroundRemovalInProgress(true);
      dispatch(setShowLoading(true));

      try {
        const response = await removeImageBackground(props.imgHashedFilename);
        if (isBGRAllowedForPAYG) {
          dispatch(updatePAYGUserImageBGRCredits(response.remainingRemoveBackgroundCredits));
        } else {
          dispatch(updateTeamCredits({credits: response.remainingRemoveBackgroundCredits}));
        }
        dispatch(setIsBackgroundRemoved(true));
      } catch (e) {
        showErrorModal(window.i18next.t('pmwjs_remove_image_bg_failed_desc'));
        return;
      } finally {
        dispatch(setShowLoading(false));
        setIsBackgroundRemovalInProgress(false);
      }

      dispatch(setIsBackgroundRemoved(true));
    } else {
      dispatch(setIsBackgroundRemoved(false));
    }
  };

  return (
    <IconBoxWithTag
      onClick={() => {
        void onBackgroundRemoveClicked();
      }}
      tagClasses="body-xxs-bold"
      className={`${styles.container} ${className}`}
      iconBoxClasses={styles.iconBoxClass}
      icon="icon-remove-bg"
      isSelected={isBackgroundRemoved}
      iconBoxText={window.PMW.isMobileScreenWidth() ? window.i18next.t('pmwjs_ai_bg_remover') : window.i18next.t('pmwjs_ai_background_remover')}
      tagText={backgroundRemovalCredits ? backgroundRemovalCredits.toString() : ''}
      tagIcon={!isPremiumUser ? 'icon-crown color-premium' : ''}
      disabled={!isPremiumUser || backgroundRemovalCredits === 0 || isBackgroundRemovalInProgress || !props.imgHashedFilename}
    />
  );
}
